import { z } from 'zod'
import { parseTimestamp } from '@/util/zod/validators'

// Stripe subscription statuses - different from our SubscriptionStatus enum which lives in our auth JWT
export enum StripeSubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAST_DUE = 'past_due',
  UNPAID = 'unpaid',
  PAUSED = 'paused',
}

// Stripe subscription interval
export enum StripeSubscriptionInterval {
  MONTH = 'month',
  YEAR = 'year',
}

export enum StripeCouponDuration {
  FOREVER = 'forever',
  REPEATING = 'repeating',
  ONCE = 'once',
}

/**
 * Stripe upcoming invoice summary
 *
 * Subset of fields from the backend response, including only what the FE uses. Once the Trial Banner
 * and Org Billing projects are complete, unused fields will be removed from the BE to align the schemas.
 */
export const StripeUpcomingInvoiceSummarySchema = z.object({
  amount_due: z.number(),
})

export type StripeUpcomingInvoiceSummary = z.infer<typeof StripeUpcomingInvoiceSummarySchema>

const StripeCouponSchema = z.object({
  percent_off: z.number().nullable(),
  duration: z.nativeEnum(StripeCouponDuration).nullable(),
})

export type StripeCoupon = z.infer<typeof StripeCouponSchema>

/**
 * Stripe customer payment details
 *
 * Subset of fields from the backend response, including only what the FE uses. Once the Trial Banner
 * and Org Billing projects are complete, unused fields will be removed from the BE to align the schemas.
 */
export const CustomerPaymentDetailsSchema = z.object({
  subscription_status: z.nativeEnum(StripeSubscriptionStatus),
  trial_end: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  interval: z.nativeEnum(StripeSubscriptionInterval),
  quantity: z.number(),
  unit_amount_cents: z.number(),
  cancel_at_period_end: z.boolean(),
  coupon: StripeCouponSchema.nullable(),
  upcoming_invoice_summary: StripeUpcomingInvoiceSummarySchema.nullable(),
  has_legacy_student_plan: z.boolean().optional(),
})

export type CustomerPaymentDetails = z.infer<typeof CustomerPaymentDetailsSchema>

// Response schema for get customer payment details endpoint
export const CustomerPaymentDetailsResponseSchema = z.object({
  customer_payment_details: CustomerPaymentDetailsSchema.nullable(),
})

export type CustomerPaymentDetailsResponse = z.infer<typeof CustomerPaymentDetailsResponseSchema>
